import {
  isItemRefSet,
  isImageRefSet,
  itemRefTargetCollectionId,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

import {
  defineCollectionTypeReference,
  defineTypeReferenceForReferenceField,
  DynamoWrapperElementMatchers,
  createCollectionListQuerySpecification,
  createItemRefSetListQuerySpecification,
  createCollectionTemplatePageQuerySpecification,
} from '@packages/systems/cms/gateway/shared';

import {
  collectionListBindingContextId,
  collectionTemplateBindingContextId,
  fieldBindingContextId,
  mifBindingContextTypeId,
} from './identifiers';

import type {
  BindingContextDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';
import type {
  Collections,
  CollectionRecordType as Collection,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';
import {
  getCollectionIdFromElement,
  getCollectionItemRefSetSlugFromElement,
  getBoundRepeaterFieldIdPathFromElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

export default function defineBindingContexts<RootTRef, EntityTRef, ListTRef>(
  collections: Collections,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  const rootBindingContext: BindingContextDef<RootTRef, EntityTRef, ListTRef> =
    {
      variant: 'Root',
      label: 'Webflow CMS',
      type: referenceBuilder.root(),
    };
  return collections.reduce<
    BindingContextDef<RootTRef, EntityTRef, ListTRef>[]
  >(
    (contexts, collection) => {
      if (collection._id && collection.slug) {
        const collectionId = collection._id;
        const collectionSlug = collection.slug;
        const typeReferenceDef = defineCollectionTypeReference(collection);

        const listBindingContextDef: BindingContextDef<
          RootTRef,
          EntityTRef,
          ListTRef
        > = {
          variant: 'List',
          id: collectionListBindingContextId(collectionId),
          label: collection.name,
          type: referenceBuilder.listOf(typeReferenceDef),
          matchElement: DynamoWrapperElementMatchers.create((element) => {
            const boundCollectionId = getCollectionIdFromElement(element);
            const isItemRefSetList = Boolean(
              getCollectionItemRefSetSlugFromElement(element)
            );
            return !isItemRefSetList && boundCollectionId === collectionId;
          }),
          connection: {
            _kind: 'LegacyDynamoWrapperConnection/Collection',
            id: collectionId,
          },
          query: createCollectionListQuerySpecification(
            collectionSlug,
            collection
          ),
        };

        const templatePageBindingContextDef: BindingContextDef<
          RootTRef,
          EntityTRef,
          ListTRef
        > = {
          variant: 'Page',
          id: collectionTemplateBindingContextId(collectionId),
          label: collection.name,
          type: referenceBuilder.entityOf(typeReferenceDef),
          matchPage: (page) => page.dynCollection === collectionId,
          query: createCollectionTemplatePageQuerySpecification(collectionSlug),
        };

        contexts.push(
          listBindingContextDef,
          templatePageBindingContextDef,
          ...defineItemRefSetFieldBindingContexts(collection, referenceBuilder),
          ...defineMultiImageFieldBindingContexts(collection, referenceBuilder)
        );
      }
      return contexts;
    },
    [rootBindingContext]
  );
}

function defineItemRefSetFieldBindingContexts<RootTRef, EntityTRef, ListTRef>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  return collection.fields.reduce<
    BindingContextDef<RootTRef, EntityTRef, ListTRef>[]
  >((contexts, field) => {
    if (isItemRefSet(field)) {
      const bindingContext = defineItemRefSetFieldBindingContext(
        field,
        collection,
        referenceBuilder
      );
      if (bindingContext) {
        contexts.push(bindingContext);
      }
    }
    return contexts;
  }, []);
}

function defineItemRefSetFieldBindingContext<RootTRef, EntityTRef, ListTRef>(
  field: Field,
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef> | null {
  const typeReference = defineTypeReferenceForReferenceField(field);

  if (!typeReference) {
    return null;
  }

  const collectionId = collection._id;
  const targetCollectionId = itemRefTargetCollectionId(field);

  if (!collectionId || !targetCollectionId) {
    return null;
  }

  return {
    variant: 'List',
    id: fieldBindingContextId(field.id, collectionId),
    label: field.name,
    type: referenceBuilder.listOf(typeReference),
    matchElement: DynamoWrapperElementMatchers.itemRefSetList(
      field.slug,
      targetCollectionId,
      collectionId
    ),
    connection: {
      _kind: 'LegacyDynamoWrapperConnection/ItemRefSet',
      id: targetCollectionId,
      itemRefSet: field.slug,
      contextCollectionId: collectionId,
    },
    query: createItemRefSetListQuerySpecification(field),
  };
}

/*
  Create BindingContext for each multi-image field of a collection
*/
function defineMultiImageFieldBindingContexts<RootTRef, EntityTRef, ListTRef>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  return collection.fields.reduce<
    BindingContextDef<RootTRef, EntityTRef, ListTRef>[]
  >((bindingContexts, field) => {
    if (isImageRefSet(field)) {
      const bindingContext = defineMultiImageFieldBindingContext(
        field,
        collection,
        referenceBuilder
      );
      if (bindingContext) {
        bindingContexts.push(bindingContext);
      }
    }

    return bindingContexts;
  }, []);
}

function defineMultiImageFieldBindingContext<RootTRef, EntityTRef, ListTRef>(
  field: Field,
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef> | null {
  if (!collection._id) {
    return null;
  }
  return {
    variant: 'List',
    id: fieldBindingContextId(field.id, collection._id),
    label: collection.name,
    type: referenceBuilder.listOf({
      entity: 'Field',
      // TODO: Address field id uniqueness
      id: mifBindingContextTypeId(field.id),
    }),
    matchElement: DynamoWrapperElementMatchers.create((element) => {
      const bindingPath = getBoundRepeaterFieldIdPathFromElement(element);
      return (
        bindingPath.length > 0 &&
        bindingPath[bindingPath.length - 1] === field.id
      );
    }),
    connection: {
      _kind: 'LegacyDynamoWrapperConnection/ImageRefSet',
      path: [
        {
          id: field.id,
          slug: field.slug,
          type: field.type,
          innerType: field.innerType,
        },
      ],
    },
  };
}
